import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { AiLoadingOverlay } from '../AiLoadingOverlay';
import { SceneRoot } from './styled';

interface Props {
  // true mientras se genera (entra); false → reproduce la salida y llama onExited.
  active: boolean;
  onExited: () => void;
  title: string;
  subtitle?: string;
  steps: string[];
}

// Escena de generación a pantalla completa, in-place (sin modal). Cubre la
// superficie de /generate y reproduce el loader "IA trabajando" (clip Remotion +
// pasos). Entrada/salida animadas con GSAP; respeta prefers-reduced-motion.
export function GenerateScene({ active, onExited, title, subtitle, steps }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Ref para no re-disparar el efecto cuando el padre recrea el callback inline.
  const exitedRef = useRef(onExited);
  exitedRef.current = onExited;

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (active) {
      if (reduce) { gsap.set(el, { opacity: 1, scale: 1 }); return; }
      const tw = gsap.to(el, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' });
      return () => { tw.kill(); };
    }

    // Salida: la escena se desvanece + escala mientras el grid de resultados
    // aparece debajo (cross-fade). Al terminar, el padre desmonta.
    if (reduce) { exitedRef.current(); return; }
    const tw = gsap.to(el, {
      opacity: 0, scale: 1.04, duration: 0.4, ease: 'power2.in',
      onComplete: () => exitedRef.current(),
    });
    return () => { tw.kill(); };
  }, [active]);

  return (
    <SceneRoot ref={rootRef}>
      <AiLoadingOverlay open title={title} subtitle={subtitle} steps={steps} variant="inline" />
    </SceneRoot>
  );
}
